/**
 * Landing 一覧ページ。
 * 「+ 新規作成」で空 landing を作成 → エディタ画面へ即遷移。
 * キャンペーン選択・詳細編集・生成はエディタ画面で行う。
 */
import { Copy, LayoutTemplate, Loader2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useCreateEmptyLanding,
  useDeleteLanding,
  useLandings,
} from '@/hooks/useLandings';

import { LandingDuplicateDialog } from './components/LandingDuplicateDialog';

function formatDate(d: unknown): string {
  if (!d) return '';
  if (typeof d === 'object' && d !== null && 'toDate' in d) {
    try {
      const date = (d as { toDate(): Date }).toDate();
      return date.toLocaleDateString('ja-JP');
    } catch {
      return '';
    }
  }
  return '';
}

export function LandingsListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data, isLoading } = useLandings();
  const createEmpty = useCreateEmptyLanding();
  const del = useDeleteLanding();
  const [dupSource, setDupSource] = useState<{ id: string; title: string } | null>(
    null,
  );

  const handleDelete = async (
    e: React.MouseEvent,
    landingId: string,
    title: string,
  ) => {
    e.stopPropagation();
    if (!confirm(`ランディング「${title}」を削除します。よろしいですか？`)) return;
    try {
      await del.mutateAsync({ landingId });
      toast.success('削除しました');
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleCreate = async () => {
    if (!user?.uid) {
      toast.error('ログインが必要です');
      return;
    }
    try {
      const id = await createEmpty.mutateAsync({ ownerId: user.uid });
      navigate(`/landings/${id}`);
    } catch (e) {
      toast.error(`作成失敗: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <Screen
      title="ランディング"
      description="キャンペーンを参照して AI でランディングページを生成・編集"
      actions={
        <Button onClick={handleCreate} disabled={createEmpty.isPending}>
          {createEmpty.isPending ? (
            <Loader2 className="size-4 mr-1 animate-spin" />
          ) : (
            <Plus className="size-4 mr-1" />
          )}{' '}
          新規作成
        </Button>
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (data?.length ?? 0) === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center gap-3">
          <LayoutTemplate className="size-12 text-[var(--text-muted)]" />
          <p className="text-[var(--text-muted)]">
            まだランディングがありません。
          </p>
          <Button onClick={handleCreate} disabled={createEmpty.isPending}>
            <Plus className="size-4 mr-1" /> 最初のランディングを作成
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {data?.map((l) => (
            <div
              key={l.id}
              className="group relative p-4 rounded-lg border border-[var(--border)] bg-[var(--bg-elevated)] hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
              onClick={() => navigate(`/landings/${l.id}`)}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <h3 className="font-semibold text-sm truncate flex-1">{l.title}</h3>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Badge variant={l.status === 'published' ? 'accent' : 'draft'}>
                    {l.status === 'published' ? '公開中' : '下書き'}
                  </Badge>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDupSource({ id: l.id, title: l.title });
                    }}
                    title="このランディングを複製"
                    className="opacity-0 group-hover:opacity-100 p-1 rounded-sm text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--bg-hover)] transition-opacity"
                  >
                    <Copy className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleDelete(e, l.id, l.title)}
                    disabled={del.isPending}
                    title="このランディングを削除"
                    className="opacity-0 group-hover:opacity-100 p-1 rounded-sm text-[var(--text-muted)] hover:text-red-500 hover:bg-[var(--bg-hover)] transition-opacity disabled:opacity-50"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              </div>
              <p className="text-xs text-[var(--text-muted)] mb-2">
                {l.brand} · {l.industry} · {l.visualIdentity}
              </p>
              <div className="text-[11px] text-[var(--text-muted)] flex items-center gap-3 flex-wrap">
                <span>{l.sections?.length ?? 0} セクション</span>
                <span>更新 {formatDate(l.updatedAt)}</span>
                {l.status === 'published' && (
                  <span className="text-[var(--accent)]">/{l.slug}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <LandingDuplicateDialog
        open={!!dupSource}
        onOpenChange={(o) => {
          if (!o) setDupSource(null);
        }}
        sourceLandingId={dupSource?.id ?? null}
        sourceTitle={dupSource?.title}
      />
    </Screen>
  );
}
